import { alpha, getContrastRatio, lighten, parseColor } from './brandColors';
import type { BrandInputs, BrandTokens } from './types/BrandFile';

/** WCAG AA contrast for normal text. */
export const MIN_TEXT_CONTRAST = 4.5;

const WHITE = '#ffffff';
const BLACK = '#000000';
const DEFAULT_TINT_PERCENT = 89;
const MAX_TINT_PERCENT = 95;
const PARTICIPANT_HUB_PRIMARY = '#3F51B5';

const stripes = (primary: string): string =>
  `repeating-linear-gradient(45deg, ${alpha(primary, 0.09)} 0 1px, transparent 1px 13px)`;

const shadowLift = (primary: string): string =>
  `0 6px 18px ${alpha(primary, 0.12)}, 0 2px 6px rgba(0,0,0,0.06)`;

/** Cards are 4px rounder than the radius; a radius of 0 keeps them square. */
const cardRadiusFor = (radius: number): number =>
  radius === 0 ? 0 : radius + 4;

/** The Participant Hub light look as brand tokens. */
export const PARTICIPANT_HUB_LIGHT_TOKENS: BrandTokens = {
  primary: PARTICIPANT_HUB_PRIMARY,
  onPrimary: WHITE,
  secondary: '#9c27b0',
  onSecondary: WHITE,
  background: '#FAFAFC',
  backgroundImage: stripes(PARTICIPANT_HUB_PRIMARY),
  surface: WHITE,
  primarySurface: '#EBECF6',
  text: 'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(0, 0, 0, 0.6)',
  textDisabled: 'rgba(0, 0, 0, 0.55)',
  textTertiary: 'rgba(0, 0, 0, 0.55)',
  shadowLift: shadowLift(PARTICIPANT_HUB_PRIMARY),
  radius: 8,
  cardRadius: cardRadiusFor(8),
};

/** The default text colour rgba(0, 0, 0, 0.87) on white as a solid colour. */
export const PARTICIPANT_HUB_SOLID_TEXT = '#212121';

type Rgb = readonly [number, number, number];

const toCss = (color: Rgb): string => `rgb(${color.join(', ')})`;

/** `top` at `opacity` painted over the opaque `bottom`. */
const blend = (top: Rgb, opacity: number, bottom: Rgb): Rgb => {
  const channel = (i: 0 | 1 | 2) =>
    Math.round(top[i] * opacity + bottom[i] * (1 - opacity));
  return [channel(0), channel(1), channel(2)];
};

/** `color` at the smallest alpha, from `defaultAlpha` up in 0.01 steps, that
 *  reaches 4.5:1 on every backdrop. */
const readableAlpha = (
  color: string,
  defaultAlpha: number,
  backdrops: readonly string[]
): string => {
  const text = parseColor(color).channels;
  const solids = backdrops.map(backdrop => parseColor(backdrop).channels);
  const isReadable = (opacity: number) =>
    solids.every(
      solid =>
        getContrastRatio(toCss(blend(text, opacity, solid)), toCss(solid)) >=
        MIN_TEXT_CONTRAST
    );
  let percent = Math.round(defaultAlpha * 100);
  while (percent < 100 && !isReadable(percent / 100)) percent += 1;
  return alpha(color, percent / 100);
};

/** Button text on `color`: white when it reaches 4.5:1, otherwise whichever
 *  of white and black reads better. */
const buttonTextOn = (color: string): string => {
  const white = getContrastRatio(WHITE, color);
  if (white >= MIN_TEXT_CONTRAST) return WHITE;
  return getContrastRatio(BLACK, color) > white ? BLACK : WHITE;
};

/** `primary` lightened by the smallest amount, from 0.89 up in 0.01 steps
 *  to at most 0.95, that keeps `primary` at 4.5:1 on it; 0.89 when `primary`
 *  is below 4.5:1 even on white. */
const readableTint = (primary: string): string => {
  if (getContrastRatio(primary, WHITE) < MIN_TEXT_CONTRAST) {
    return lighten(primary, DEFAULT_TINT_PERCENT / 100);
  }
  let percent = DEFAULT_TINT_PERCENT;
  while (
    percent < MAX_TINT_PERCENT &&
    getContrastRatio(primary, lighten(primary, percent / 100)) <
      MIN_TEXT_CONTRAST
  )
    percent += 1;
  return lighten(primary, percent / 100);
};

/** Every light token of a brand: its inputs, the defaults for what it does
 *  not set, and the tints, shadow, button text and readable translucent text
 *  derived from them. Every brand runs these rules, also one that sets only a
 *  logo. */
export const computeBrandTokens = (
  inputs: BrandInputs,
  defaults: BrandTokens = PARTICIPANT_HUB_LIGHT_TOKENS
): BrandTokens => {
  const { colors } = inputs;
  const primary = colors.primary ?? defaults.primary;
  const background = colors.background ?? defaults.background;
  const surface = colors.surface ?? defaults.surface;
  const text = colors.text ?? defaults.text;
  const primarySurface = colors.primary
    ? readableTint(colors.primary)
    : defaults.primarySurface;
  const backdrops = [background, surface, primarySurface];
  const mutedText = readableAlpha(text, 0.54, backdrops);
  const radius = inputs.borderRadius ?? defaults.radius;

  return {
    primary,
    onPrimary: colors.primary
      ? buttonTextOn(colors.primary)
      : defaults.onPrimary,
    secondary: colors.secondary ?? defaults.secondary,
    onSecondary: colors.secondary
      ? buttonTextOn(colors.secondary)
      : defaults.onSecondary,
    background,
    backgroundImage: colors.background ? 'none' : stripes(primary),
    surface,
    primarySurface,
    text,
    textSecondary: readableAlpha(text, 0.6, backdrops),
    textDisabled: mutedText,
    textTertiary: mutedText,
    shadowLift: colors.primary
      ? shadowLift(colors.primary)
      : defaults.shadowLift,
    radius,
    cardRadius: cardRadiusFor(radius),
  };
};
